import { useState, FormEvent } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/base/SchemaOrg';
import SEOMeta from '@/components/base/SEOMeta';
import { siteInfo } from '@/mocks/siteInfo';

const FORM_URL = 'https://readdy.ai/api/form/d9pj7uli2vhsdh2d35sg';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('phone_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      setFormMessage('消息已发送，我们会尽快回复您！');
      form.reset();
      return;
    }

    formData.delete('phone_alt');

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        params.append(key, value as string);
      });

      const response = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // ignore parse errors
      }

      const serverMsg = (parsed?.meta as Record<string, unknown>)?.message as string
        || (parsed?.message as string)
        || (parsed?.meta as Record<string, unknown>)?.detail as string
        || responseText;

      if (response.ok && (parsed?.code === 'OK')) {
        setFormStatus('success');
        setFormMessage('消息已发送，我们会尽快回复您！');
        form.reset();
      } else {
        setFormStatus('error');
        setFormMessage(serverMsg || '发送失败，请稍后重试');
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请检查网络连接后重试');
    }
  };

  return (
    <>
      <ScrollToTop />
      <SEOMeta
        title="关于我们 - 91com官方平台"
        description={`了解91com官方平台的品牌故事、发展历程、用户口碑与联系方式。${siteInfo.description}`}
        keywords="91com,关于91com,91com官方平台,91com联系方式"
        canonicalUrl={`${siteInfo.url}/about`}
        ogTitle="关于我们 - 91com官方平台"
        ogDescription={`了解91com官方平台的品牌故事、发展历程、用户口碑与联系方式。`}
      />
      <SchemaOrg pageType="about" />

      <Navbar />

      <main className="pt-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb items={[{ label: '关于我们' }]} />

          {/* Page Header */}
          <section className="py-10 text-center">
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 mb-3">
              关于<strong>91com</strong>
            </h1>
            <p className="text-sm text-foreground-600 max-w-xl mx-auto">
              致力于为用户提供安全、便捷、优质的在线平台体验
            </p>
          </section>

          {/* Brand Story */}
          <section className="py-10 md:py-14">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4">
                  品牌故事
                </h2>
                <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                  <strong>91com官方平台</strong>成立于{siteInfo.founded}年，自上线以来始终秉承"用户至上、安全第一"的理念，致力于为广大用户打造一个安全可靠、功能丰富的在线服务平台。
                </p>
                <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                  经过多年的持续发展和技术积累，<strong>91com</strong>已累计服务超过{siteInfo.usersCount}用户，涵盖多个领域的优质内容与服务，成为备受用户信赖的热门平台。
                </p>
                <p className="text-sm text-foreground-700 leading-relaxed">
                  我们拥有一支专业的技术和运营团队，持续优化平台功能、提升用户体验，力求为每一位用户提供最优质的服务。无论您是新用户还是老朋友，<strong>91com</strong>都将是您值得信赖的在线伙伴。
                </p>
              </div>
              <div className="relative h-72 md:h-80 rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20clean%20office%20workspace%20with%20warm%20orange%20and%20teal%20accents%20team%20collaboration%20professional%20technology%20company%20atmosphere%20bright%20natural%20light%20minimalist%20Chinese%20tech%20style&width=800&height=600&seq=about-brand-story&orientation=landscape"
                  alt="91com团队"
                  title="91com官方平台团队"
                  className="w-full h-full object-cover object-top rounded-lg"
                />
              </div>
            </div>
          </section>

          {/* Stats */}
          <section className="py-10 md:py-14 bg-background-100 rounded-lg px-6 md:px-10">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { value: siteInfo.usersCount, label: '累计服务用户' },
                { value: siteInfo.rating, label: '用户满意度评分' },
                { value: siteInfo.reviewsCount, label: '累计好评数量' },
                { value: new Date().getFullYear() - 2018, label: '年行业经验' },
              ].map((stat) => (
                <div key={stat.label} className="text-center">
                  <div className="text-2xl md:text-3xl font-bold text-primary-500 mb-1">
                    {stat.value}
                  </div>
                  <p className="text-xs text-foreground-600">{stat.label}</p>
                </div>
              ))}
            </div>
          </section>

          {/* User Reviews */}
          <section className="py-10 md:py-14">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2 text-center">
              用户口碑
            </h2>
            <p className="text-sm text-foreground-600 text-center mb-10">
              来自真实用户的使用评价与反馈
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                {
                  name: '张先生',
                  role: '资深用户 · 使用3年',
                  comment: '91com平台使用体验非常棒，界面简洁，功能齐全，安全方面也让人很放心。客服响应速度很快，遇到问题都能及时解决。',
                  rating: 5,
                },
                {
                  name: '李女士',
                  role: '活跃用户 · 使用2年',
                  comment: '从朋友推荐开始使用91com，到现在已经成为日常必备的平台了。内容丰富，社区氛围也很好，强烈推荐！',
                  rating: 5,
                },
                {
                  name: '王先生',
                  role: '新用户 · 使用6个月',
                  comment: '作为新手，91com的使用教程非常详细，注册和上手都很顺利。平台功能强大但操作简单，对新用户很友好。',
                  rating: 5,
                },
              ].map((review) => (
                <div key={review.name} className="bg-background-50 border border-background-200/70 rounded-lg p-6">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: review.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                    &ldquo;{review.comment}&rdquo;
                  </p>
                  <div>
                    <p className="text-sm font-semibold text-foreground-900">{review.name}</p>
                    <p className="text-xs text-foreground-500">{review.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Values */}
          <section className="py-10 md:py-14 bg-background-100 rounded-lg px-6 md:px-10 mb-10">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2 text-center">
              核心价值观
            </h2>
            <p className="text-sm text-foreground-600 text-center mb-10">
              指引<strong>91com</strong>不断前行的信念
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: 'ri-heart-pulse-line', title: '用户至上', desc: '始终将用户体验放在首位，倾听用户声音，持续优化改进。' },
                { icon: 'ri-shield-star-line', title: '安全第一', desc: '投入大量资源保障平台和数据安全，让用户用得放心。' },
                { icon: 'ri-lightbulb-flash-line', title: '创新驱动', desc: '积极探索新技术、新功能，为用户带来更优质的服务体验。' },
                { icon: 'ri-hand-heart-line', title: '诚信为本', desc: '坚持诚信经营，以真诚和专业赢得每一位用户的信任。' },
              ].map((value) => (
                <div key={value.title} className="text-center">
                  <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-primary-100 mb-4">
                    <i className={`${value.icon} text-primary-600 text-xl`}></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-2">{value.title}</h3>
                  <p className="text-sm text-foreground-600">{value.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Contact Section */}
          <section id="contact" className="py-10 md:py-14">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2 text-center">
              联系我们
            </h2>
            <p className="text-sm text-foreground-600 text-center mb-10">
              如有任何问题或建议，欢迎通过以下方式与我们取得联系
            </p>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              <div>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                      <i className="ri-map-pin-line text-primary-600 text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900 mb-1">公司地址</h3>
                      <p className="text-sm text-foreground-600">{siteInfo.address}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                      <i className="ri-phone-line text-primary-600 text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900 mb-1">客服热线</h3>
                      <p className="text-sm text-foreground-600">{siteInfo.phone}</p>
                      <p className="text-xs text-foreground-500">7×24小时全天候服务</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                      <i className="ri-mail-line text-primary-600 text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h3>
                      <p className="text-sm text-foreground-600">{siteInfo.email}</p>
                      <p className="text-xs text-foreground-500">我们将在24小时内回复</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-background-100 rounded-lg p-6 border border-background-200/70">
                <h3 className="text-base font-semibold text-foreground-900 mb-4">发送消息</h3>
                <form className="space-y-4" onSubmit={handleSubmit} data-readdy-form="">
                  <div>
                    <input
                      type="text"
                      name="name"
                      placeholder="您的姓名"
                      className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                      required
                    />
                  </div>
                  <div>
                    <input
                      type="email"
                      name="email"
                      placeholder="您的邮箱"
                      className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                      required
                    />
                  </div>
                  <div>
                    <textarea
                      name="message"
                      placeholder="请输入您的问题或建议（500字以内）"
                      rows={4}
                      maxLength={500}
                      className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors resize-none"
                      required
                    ></textarea>
                  </div>
                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="honeypot-field"
                  />
                  {formStatus !== 'idle' && (
                    <div className={`text-sm px-4 py-2.5 rounded-md ${
                      formStatus === 'success'
                        ? 'bg-accent-50 text-accent-700 border border-accent-200'
                        : 'bg-red-50 text-red-700 border border-red-200'
                    }`}>
                      {formMessage}
                    </div>
                  )}
                  <button
                    type="submit"
                    className="w-full py-2.5 bg-primary-500 text-background-50 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                  >
                    发送消息
                  </button>
                </form>
              </div>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </>
  );
}